/**
 * Single source of truth for the DRSP instrument.
 *
 * Wording transcribed verbatim from the paper form at
 * docs/reference/drsp-daily-record-of-severity-of-problems.pdf
 * "Daily Record of Severity of Problems", (c) 1997 Jean Endicott, Ph.D. and
 * Wilma Harrison, M.D. See NOTICE.
 *
 * Imported by the entry UI, the charts and the spreadsheet export so that item
 * text and ordering are defined exactly once.
 */

export type Severity = 1 | 2 | 3 | 4 | 5 | 6

/** Item numbers 1..14, matching the order of rows on the paper form. */
export type ItemId = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14

export interface DrspItem {
  id: ItemId
  /** Full wording as printed on the form. */
  text: string
  /** Condensed label for the phone entry screen. */
  short: string
  /**
   * Items 1-11 are symptoms and are summed into the daily total.
   * Items 12-14 rate functional impairment and are reported separately, as on
   * the paper form where they sit below the numbered block.
   */
  kind: 'symptom' | 'impairment'
}

export const ITEMS: readonly DrspItem[] = [
  {
    id: 1,
    text: 'Felt depressed, sad, "down," or "blue"; or felt hopeless; or felt worthless or guilty',
    short: 'Depressed, sad, hopeless',
    kind: 'symptom',
  },
  {
    id: 2,
    text: 'Felt anxious, tense, "keyed up" or "on edge"',
    short: 'Anxious, tense',
    kind: 'symptom',
  },
  {
    id: 3,
    text: 'Had mood swings (i.e., suddenly feeling sad or tearful) or was sensitive to rejection or feelings were easily hurt',
    short: 'Mood swings, sensitive',
    kind: 'symptom',
  },
  {
    id: 4,
    text: 'Felt angry, or irritable',
    short: 'Angry, irritable',
    kind: 'symptom',
  },
  {
    id: 5,
    text: 'Had less interest in usual activities (work, school, friends, hobbies)',
    short: 'Less interest in activities',
    kind: 'symptom',
  },
  {
    id: 6,
    text: 'Had difficulty concentrating',
    short: 'Difficulty concentrating',
    kind: 'symptom',
  },
  {
    id: 7,
    text: 'Felt lethargic, tired, or fatigued; or had lack of energy',
    short: 'Lethargic, tired',
    kind: 'symptom',
  },
  {
    id: 8,
    text: 'Had increased appetite or overate; or had cravings for specific foods',
    short: 'Appetite, cravings',
    kind: 'symptom',
  },
  {
    id: 9,
    text: 'Slept more, took naps, found it hard to get up when intended; or had trouble getting to sleep or staying asleep',
    short: 'Sleep problems',
    kind: 'symptom',
  },
  {
    id: 10,
    text: 'Felt overwhelmed or unable to cope; or felt out of control',
    short: 'Overwhelmed, out of control',
    kind: 'symptom',
  },
  {
    id: 11,
    text: 'Had breast tenderness, breast swelling, bloated sensation, weight gain, headache, joint or muscle pain, or other physical symptoms',
    short: 'Physical symptoms',
    kind: 'symptom',
  },
  {
    id: 12,
    text: 'At work, school, home, or in daily routine, at least one of the problems noted above caused reduction of productivity or inefficiency',
    short: 'Reduced productivity',
    kind: 'impairment',
  },
  {
    id: 13,
    text: 'At least one of the problems noted above caused avoidance of or less participation in hobbies or social activities',
    short: 'Avoided hobbies, social',
    kind: 'impairment',
  },
  {
    id: 14,
    text: 'At least one of the problems noted above interfered with relationships with others',
    short: 'Relationships affected',
    kind: 'impairment',
  },
] as const

export const SYMPTOM_ITEMS = ITEMS.filter((i) => i.kind === 'symptom')
export const IMPAIRMENT_ITEMS = ITEMS.filter((i) => i.kind === 'impairment')

/** Severity legend, exactly as printed: "1 - not at all, 2 - minimal, ...". */
export const SEVERITY_LABELS: Record<Severity, string> = {
  1: 'not at all',
  2: 'minimal',
  3: 'mild',
  4: 'moderate',
  5: 'severe',
  6: 'extreme',
}

export const SEVERITIES: readonly Severity[] = [1, 2, 3, 4, 5, 6]

/** Column name in the `entries` table for a given item, e.g. 3 -> "item_03". */
export function itemColumn(id: ItemId): string {
  return `item_${String(id).padStart(2, '0')}`
}

export const ITEM_COLUMNS = ITEMS.map((i) => itemColumn(i.id))

/** Day-of-week letters as the form specifies: Monday="M", Thursday="R", etc. */
export const DAY_LETTERS = ['U', 'M', 'T', 'W', 'R', 'F', 'S'] as const

export const COPYRIGHT_LINE =
  '© 1997, Jean Endicott, Ph.D. and Wilma Harrison, M.D.'

export const INSTRUCTION_LINE =
  'Each evening note the degree to which you experienced each of the problems listed below. Put an "x" in the box which corresponds to the severity: 1 - not at all, 2 - minimal, 3 - mild, 4 - moderate, 5 - severe, 6 - extreme.'
